import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Wifi, WifiOff, RefreshCw, ExternalLink, CheckCircle2, AlertCircle,
  Send, Phone, Smartphone, Book, ShoppingBag, Calendar, Loader2,
  Activity, BellOff,
} from "lucide-react";

export default function GuideIntegrationTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeView, setActiveView] = useState("overview");
  const [monitor, setMonitor] = useState(null);
  const [monitorHistory, setMonitorHistory] = useState([]);
  const [pingingNow, setPingingNow] = useState(false);

  const loadMonitor = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/admin/guide/monitor/status"),
        api.get("/admin/guide/monitor/history?hours=24"),
      ]);
      setMonitor(s.data);
      setMonitorHistory(h.data.checks || []);
    } catch {/* silent */}
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/guide/status");
      setStatus(r.data);
      if (r.data?.configured) {
        const [rm, sv, bk, ev] = await Promise.all([
          api.get("/admin/guide/rooms").catch(() => ({ data: { rooms: [] } })),
          api.get("/admin/guide/services").catch(() => ({ data: { services: [] } })),
          api.get("/admin/guide/bookings").catch(() => ({ data: { bookings: [] } })),
          api.get("/admin/guide/events").catch(() => ({ data: { events: [] } })),
        ]);
        setRooms(rm.data.rooms || []);
        setServices(sv.data.services || []);
        setBookings(bk.data.bookings || []);
        setEvents(ev.data.events || []);
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore connessione app guida"));
      setStatus({ configured: false, error: e.response?.data?.detail });
    } finally {
      setLoading(false);
    }
    loadMonitor();
  };

  useEffect(() => { refresh(); }, []);

  const pingNow = async () => {
    setPingingNow(true);
    try {
      const r = await api.post("/admin/guide/monitor/run-now");
      if (r.data.ok) toast.success(`Ping OK · ${r.data.latency_ms} ms`);
      else toast.error(`Ping fallito: ${r.data.error || "errore"}`);
      loadMonitor();
    } catch {
      toast.error("Errore ping");
    } finally {
      setPingingNow(false);
    }
  };

  const ackAlerts = async () => {
    try {
      await api.post("/admin/guide/monitor/acknowledge");
      toast.success("Alert segnati come letti");
      loadMonitor();
    } catch {/* silent */}
  };

  const syncAll = async () => {
    if (!window.confirm("Sincronizzare tutte le prenotazioni con l'app guida?")) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/guide/sync/all");
      const { total, ok, failed } = r.data;
      if (failed > 0) {
        toast.warning(`Sincronizzate ${ok}/${total} · ${failed} fallite`);
      } else {
        toast.success(`Sincronizzate ${ok} prenotazioni`);
      }
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore sincronizzazione"));
    } finally {
      setSyncing(false);
    }
  };

  const isOnline = status?.configured && status?.health?.ok;

  return (
    <div className="space-y-5" data-testid="guide-integration-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Book className="w-6 h-6 text-[#AC5D45]" />
            App Guida Digitale
          </h2>
          <p className="text-sm text-[#635F59] mt-0.5">
            Connessione con <strong>booking-guide-plus</strong> — sincronizza prenotazioni, ospiti e servizi extra.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="guide-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Aggiorna
          </Button>
          {status?.url && (
            <a
              href={status.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-[#E8E2D9] rounded-md hover:bg-[#FDFBF7]"
              data-testid="guide-open-app"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Apri app guida
            </a>
          )}
          <Button
            onClick={syncAll}
            disabled={syncing || !isOnline}
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            data-testid="guide-sync-all"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Sincronizza tutte le prenotazioni
          </Button>
        </div>
      </div>

      {/* Connection status */}
      <Card
        className={`p-4 flex items-start gap-3 border-2 ${
          isOnline
            ? "border-emerald-300 bg-emerald-50"
            : "border-rose-300 bg-rose-50"
        }`}
        data-testid="guide-status-card"
      >
        {isOnline ? (
          <Wifi className="w-6 h-6 text-emerald-700 mt-0.5" />
        ) : (
          <WifiOff className="w-6 h-6 text-rose-700 mt-0.5" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold ${isOnline ? "text-emerald-800" : "text-rose-800"}`}>
              {isOnline ? "Connessione attiva" : "Non connesso"}
            </span>
            {status?.health?.version && (
              <Badge variant="outline" className="bg-white text-emerald-700 border-emerald-300">
                {status.health.app} v{status.health.version}
              </Badge>
            )}
          </div>
          <div className="text-xs text-[#635F59] mt-1 break-all">
            <span className="font-mono">{status?.url || "URL non configurato"}</span>
          </div>
          {status?.config && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-1.5 text-xs">
              <div className="text-[#635F59]">
                B&amp;B: <strong className="text-[#1E3A5F]">{status.config.bnb_name || "—"}</strong>
              </div>
              <div className="text-[#635F59]">
                Host: <strong className="text-[#1E3A5F]">{status.config.host_name || "—"}</strong>
              </div>
              <div className="text-[#635F59] inline-flex items-center gap-1">
                <Phone className="w-3 h-3" /> <strong className="text-[#1E3A5F]">{status.config.host_whatsapp || "—"}</strong>
              </div>
            </div>
          )}
          {isOnline && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full border border-emerald-300">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              Auto-sync attivo · ogni conferma viene pubblicata automaticamente
            </div>
          )}
          {!isOnline && status?.error && (
            <div className="text-xs text-rose-700 mt-1 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5" />
              {status.error}
            </div>
          )}
        </div>
      </Card>

      {/* Monitor widget — uptime + last 24h sparkline */}
      {monitor && (
        <Card className="p-4 border border-[#E8E2D9] bg-white" data-testid="guide-monitor-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${monitor.last_check?.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1E3A5F]">Monitor automatico</h3>
                <p className="text-xs text-[#635F59]">
                  Ping ogni 5 min · ricevi email se l'app guida va offline
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {monitor.alerts_unread > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={ackAlerts}
                  data-testid="guide-monitor-ack"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                >
                  <BellOff className="w-3.5 h-3.5 mr-1" />
                  Segna come letti ({monitor.alerts_unread})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={pingNow}
                disabled={pingingNow}
                data-testid="guide-monitor-ping"
              >
                {pingingNow ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Ping ora
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <MiniStat
              label="Stato attuale"
              value={monitor.last_check?.ok ? "Online" : "Offline"}
              valueClass={monitor.last_check?.ok ? "text-emerald-700" : "text-rose-700"}
              testId="monitor-current"
            />
            <MiniStat
              label="Uptime 24h"
              value={monitor.uptime_24h_pct != null ? `${monitor.uptime_24h_pct}%` : "—"}
              valueClass={
                monitor.uptime_24h_pct == null ? "text-slate-500"
                : monitor.uptime_24h_pct >= 99 ? "text-emerald-700"
                : monitor.uptime_24h_pct >= 95 ? "text-amber-700"
                : "text-rose-700"
              }
              testId="monitor-uptime"
            />
            <MiniStat
              label="Latenza"
              value={monitor.last_check?.latency_ms != null ? `${monitor.last_check.latency_ms} ms` : "—"}
              valueClass="text-[#1E3A5F]"
              testId="monitor-latency"
            />
            <MiniStat
              label="Probe (24h)"
              value={monitor.checks_24h ?? 0}
              valueClass="text-[#1E3A5F]"
              testId="monitor-checks"
            />
          </div>

          {/* Sparkline timeline last 24h */}
          {monitorHistory.length > 0 && (
            <div className="border-t border-[#E8E2D9] pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs text-[#635F59] uppercase tracking-wider">Ultime 24h</div>
                <div className="text-xs text-[#635F59]">
                  <span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm mr-1"></span> online
                  <span className="inline-block w-2 h-2 bg-rose-500 rounded-sm ml-3 mr-1"></span> offline
                </div>
              </div>
              <div className="flex gap-[2px] h-8 items-end overflow-hidden">
                {monitorHistory.slice(0, 100).reverse().map((c, i) => (
                  <div
                    key={`mon-${c.timestamp}-${i}`}
                    title={`${new Date(c.timestamp).toLocaleString("it-IT")} · ${c.ok ? "OK" : "FAIL"}${c.latency_ms ? ` · ${c.latency_ms}ms` : ""}`}
                    className={`flex-1 min-w-[3px] rounded-sm ${c.ok ? "bg-emerald-500" : "bg-rose-500"}`}
                    style={{ height: c.ok ? `${Math.min(100, 30 + (c.latency_ms || 0) / 20)}%` : "100%" }}
                  />
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Prenotazioni in guida" value={bookings.length} color="navy" testId="stat-guide-bookings" />
        <StatCard icon={Book} label="Camere caricate" value={rooms.length} color="emerald" testId="stat-guide-rooms" />
        <StatCard icon={ShoppingBag} label="Servizi extra" value={services.length} color="amber" testId="stat-guide-services" />
        <StatCard icon={Smartphone} label="WhatsApp host" value={status?.config?.host_whatsapp ? "OK" : "—"} color="rose" testId="stat-guide-wa" />
      </div>

      {/* Subview tabs */}
      <div className="border-b border-[#E8E2D9] flex gap-6 text-sm flex-wrap">
        {[
          { id: "overview", label: "Panoramica" },
          { id: "bookings", label: `Prenotazioni in guida (${bookings.length})` },
          { id: "checkins", label: `Check-in ospiti (${events.filter((e) => e.event === "guest.checkin").length})` },
          { id: "orders", label: `Servizi ordinati (${events.filter((e) => e.event === "service.ordered").length})` },
          { id: "rooms", label: `Camere (${rooms.length})` },
          { id: "services", label: `Servizi (${services.length})` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveView(t.id)}
            className={`pb-2 -mb-px transition border-b-2 ${activeView === t.id ? "border-[#AC5D45] text-[#AC5D45] font-semibold" : "border-transparent text-[#635F59] hover:text-[#1E3A5F]"}`}
            data-testid={`guide-view-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeView === "overview" && (
        <>
          <Card className="p-6 bg-[#FDFBF7] border-[#E8E2D9]">
            <h3 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Come funziona
            </h3>
            <ul className="space-y-2 text-sm text-[#2D2926]">
              <li>• <strong>Auto-sync attivo</strong>: ogni volta che confermi una prenotazione qui, viene pubblicata automaticamente nell'app guida.</li>
              <li>• L'ospite riceve l'accesso alla guida digitale via WhatsApp/email con tutte le info del soggiorno.</li>
              <li>• Quando un ospite completa il check-in (Alloggiati Web) nell'app, lo vedi nella scheda <em>Check-in ospiti</em>.</li>
              <li>• Quando un ospite ordina un servizio extra (parking, lavanderia...), lo vedi nella scheda <em>Servizi ordinati</em>.</li>
              <li>• Puoi sempre forzare una nuova sincronizzazione con il bottone <strong>"Sincronizza tutte"</strong>.</li>
            </ul>
          </Card>
          <Card className="p-6 bg-white border-[#E8E2D9]">
            <h3 className="font-semibold text-[#1E3A5F] mb-2 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-[#AC5D45]" /> Webhook inverso (one-time setup)
            </h3>
            <p className="text-sm text-[#635F59] mb-2">
              Vai nell'app booking-guide-plus → <em>Impostazioni → Webhook</em> e incolla questo URL:
            </p>
            <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 font-mono text-xs flex items-center justify-between gap-2 break-all">
              <span data-testid="guide-webhook-url">
                {(window?.location?.origin || "https://accommodation-plus.preview.emergentagent.com")}/api/admin/guide/webhook
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const url = `${window.location.origin}/api/admin/guide/webhook`;
                  const ok = await copyToClipboard(url);
                  if (ok) toast.success("URL copiato negli appunti");
                  else toast.error("Copia non riuscita");
                }}
                data-testid="guide-copy-webhook"
              >Copia</Button>
            </div>
            <p className="text-[10px] text-[#635F59] mt-2">Eventi gestiti: <code>guest.checkin</code> · <code>service.ordered</code> · <code>booking.updated</code></p>
          </Card>
        </>
      )}

      {activeView === "bookings" && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-x-auto">
          <Table data-testid="guide-bookings-table">
            <TableHeader>
              <TableRow>
                <TableHead>Codice</TableHead>
                <TableHead>Ospite</TableHead>
                <TableHead>Camera</TableHead>
                <TableHead>Arrivo</TableHead>
                <TableHead>Partenza</TableHead>
                <TableHead>Notti</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-6 text-[#635F59]">Nessuna prenotazione nell'app guida.</TableCell></TableRow>
              ) : bookings.map((b) => (
                <TableRow key={b.id || b.external_id}>
                  <TableCell className="font-mono text-xs text-[#0071C2]">{b.external_id}</TableCell>
                  <TableCell className="text-sm">{b.guest_name || "—"}</TableCell>
                  <TableCell className="text-xs">{b.room_number || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{b.arrival_date || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{b.departure_date || "—"}</TableCell>
                  <TableCell className="text-center">{b.nights || 0}</TableCell>
                  <TableCell className="text-right font-semibold text-[#AC5D45]">€ {Number(b.amount || 0).toFixed(2)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{b.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {activeView === "checkins" && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-x-auto" data-testid="guide-checkins-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Codice prenotazione</TableHead>
                <TableHead>Ospite</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const items = events.filter((e) => e.event === "guest.checkin");
                if (items.length === 0) {
                  return <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#635F59]">Nessun check-in registrato dall'app guida.</TableCell></TableRow>;
                }
                return items.map((e, i) => {
                  const p = e.payload || {};
                  const g = p.guest || {};
                  return (
                    <TableRow key={`ci-${i}`}>
                      <TableCell className="text-xs font-mono">{(e.received_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="font-mono text-xs text-[#0071C2]">{p.external_id || p.booking_code || "—"}</TableCell>
                      <TableCell className="text-sm">{g.name || g.full_name || `${g.first_name || ""} ${g.last_name || ""}`.trim() || "—"}</TableCell>
                      <TableCell className="text-xs">{g.document_type ? `${g.document_type} ${g.document_number || ""}` : "—"}</TableCell>
                      <TableCell className="text-xs text-[#635F59]">{p.notes || g.country || ""}</TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </div>
      )}

      {activeView === "orders" && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-x-auto" data-testid="guide-orders-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Prenotazione</TableHead>
                <TableHead>Servizio</TableHead>
                <TableHead className="text-right">Prezzo</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const items = events.filter((e) => e.event === "service.ordered");
                if (items.length === 0) {
                  return <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#635F59]">Nessun servizio extra ordinato dall'app guida.</TableCell></TableRow>;
                }
                return items.map((e, i) => {
                  const p = e.payload || {};
                  const s = p.service || {};
                  return (
                    <TableRow key={`ord-${i}`}>
                      <TableCell className="text-xs font-mono">{(e.received_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="font-mono text-xs text-[#0071C2]">{p.external_id || p.booking_code || "—"}</TableCell>
                      <TableCell className="text-sm">{s.label || s.name || s.code || "—"}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-[#AC5D45]">€ {Number(s.price || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs text-[#635F59]">{p.notes || ""}</TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </div>
      )}

      {activeView === "rooms" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rooms.length === 0 ? (
            <p className="text-sm text-[#635F59] italic">Nessuna camera caricata nell'app guida.</p>
          ) : rooms.map((r) => (
            <Card key={r.id || r.code} className="p-3 border-[#E8E2D9]">
              <div className="font-medium text-[#1E3A5F]">{r.name || r.label || r.code}</div>
              {r.description && <div className="text-xs text-[#635F59] mt-1">{r.description}</div>}
            </Card>
          ))}
        </div>
      )}

      {activeView === "services" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.length === 0 ? (
            <p className="text-sm text-[#635F59] italic">Nessun servizio extra configurato.</p>
          ) : services.map((s) => (
            <Card key={s.id || s.code} className="p-3 border-[#E8E2D9] flex items-center justify-between">
              <div>
                <div className="font-medium text-[#1E3A5F]">{s.label || s.name}</div>
                <div className="text-[10px] text-[#635F59] font-mono">{s.code}</div>
              </div>
              <div className="font-semibold text-[#AC5D45]">
                € {Number(s.price || 0).toFixed(2)}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testId }) => {
  const colorMap = {
    rose: "text-rose-600 bg-rose-50 border-rose-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    navy: "text-[#1E3A5F] bg-[#FDFBF7] border-[#E8E2D9]",
  };
  return (
    <div className={`border rounded-xl p-4 flex items-center gap-3 ${colorMap[color]}`} data-testid={testId}>
      <Icon className="w-7 h-7" />
      <div>
        <div className="font-display text-2xl leading-none">{value}</div>
        <div className="text-[11px] mt-0.5 opacity-80">{label}</div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, valueClass, testId }) => (
  <div className="border border-[#E8E2D9] rounded-lg p-2.5 bg-[#FDFBF7]" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-[#635F59]">{label}</div>
    <div className={`font-display text-xl mt-0.5 ${valueClass || ""}`}>{value}</div>
  </div>
);

